<template>
  <div
    class="time-range"
    :class="{
      resizing: isResizing,
      'can-show-tooltip-on-hover': canShowTooltipOnHover,
      preview,
      selecting,
      readonly: isReadOnly,
    }"
    :style="{
      gridRowStart: gridPosition.rowStart,
      gridColumnStart: gridPosition.columnStart,
      gridRowEnd: gridPosition.rowEnd,
      gridColumnEnd: gridPosition.columnEnd,
      marginLeft: marginLeft,
      marginRight: marginRight,
    }"
    @mouseenter="isMouseOver = true"
    @mouseleave="isMouseOver = false">
    <template v-if="isMouseOver || isResizing">
      <div
        class="left-handle"
        :class="{ disabled: isReadOnly }"
        @mousedown.stop="leftHandleClicked">
        <div class="handle-content">
          <span
            ref="fromTooltip"
            class="timestamp-tooltip"
            >{{ from.toString('minute') }}</span
          >
        </div>
      </div>
      <div
        class="right-handle"
        :class="{ disabled: isReadOnly }"
        @mousedown.stop="rightHandleClicked">
        <div class="handle-content">
          <span
            ref="toTooltip"
            class="timestamp-tooltip"
            :class="{ flipped: areTooltipsOverlapping() }"
            >{{ to.toString('minute') }}</span
          >
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, Ref, ref } from 'vue';

  import { Time } from '@/common/models/Time';
  import TimeRange from '@/common/models/TimeRange';

  import GridAction from './GridAction.enum';

  export interface IRangeResizeHandler {
    stop(): void;
    getDayIndex(): number;
    getOriginalRange(): TimeRange;
    getResizedRange(): TimeRange;
    updateRange(time: Time): void;
  }

  interface IRangeProps {
    day: number;
    timeRange: TimeRange;
    gridAction?: GridAction;
    preview?: boolean;
    selecting?: boolean;
    isReadOnly?: boolean;
  }

  const props = defineProps<IRangeProps>();

  const emits = defineEmits<{
    (e: 'resizeStarted', value: IRangeResizeHandler): void;
  }>();

  const resizedSide: Ref<'to' | 'from'> = ref();
  const isResizing = computed(() => resizedSide.value != null);
  const resizedTo: Ref<Time> = ref();
  const resizedFrom: Ref<Time> = ref();

  const isMouseOver: Ref<boolean> = ref(false);
  const fromTooltip: Ref<HTMLElement> = ref();
  const toTooltip: Ref<HTMLElement> = ref();

  const to = computed(() => {
    if (resizedSide.value === 'to') {
      return resizedTo.value ?? props.timeRange.to;
    }
    return props.timeRange.to;
  });

  const from = computed(() => {
    if (resizedSide.value === 'from') {
      return resizedFrom.value ?? props.timeRange.from;
    }
    return props.timeRange.from;
  });

  const canShowTooltipOnHover = computed(() => props.gridAction === GridAction.None);

  const gridPosition = computed(() => ({
    rowStart: props.day + 1,
    rowEnd: props.day + 2,
    columnStart: Math.floor(from.value.toHours()) + 1,
    columnEnd: Math.ceil(to.value.toHours()) + 1,
  }));

  const columnSpan = computed(() => gridPosition.value.columnEnd - gridPosition.value.columnStart);

  const marginLeft = computed(() => {
    if (from.value.minutes === 0) {
      return '0%';
    }
    const totalSpan = columnSpan.value;
    const marginSpan = from.value.minutes / 60;
    const margin = (marginSpan / totalSpan) * 100;
    return `${margin}%`;
  });

  const marginRight = computed(() => {
    if (to.value.minutes === 0) {
      return '0%';
    }
    const totalSpan = columnSpan.value;
    const marginSpan = (60 - to.value.minutes) / 60;
    const margin = (marginSpan / totalSpan) * 100;
    return `${margin}%`;
  });

  const resizeHandler: IRangeResizeHandler = {
    stop() {
      resizedSide.value = null;
      resizedFrom.value = null;
      resizedTo.value = null;
    },
    getDayIndex() {
      return props.day;
    },
    getOriginalRange() {
      return props.timeRange;
    },
    getResizedRange() {
      return TimeRange.fromObject([from.value, to.value]);
    },
    updateRange(time: Time) {
      if (resizedSide.value === 'from') {
        if (time >= props.timeRange.to) {
          return;
        }
        resizedFrom.value = time;
      }
      if (resizedSide.value === 'to') {
        if (time <= props.timeRange.from) {
          return;
        }
        resizedTo.value = time;
      }
    },
  };

  function leftHandleClicked() {
    if (props.isReadOnly) {
      return;
    }
    resizedSide.value = 'from';
    emits('resizeStarted', resizeHandler);
  }

  function rightHandleClicked() {
    if (props.isReadOnly) {
      return;
    }
    resizedSide.value = 'to';
    emits('resizeStarted', resizeHandler);
  }

  function areTooltipsOverlapping() {
    if (fromTooltip.value == null || toTooltip.value == null) {
      return false;
    }
    const fromRect = fromTooltip.value.getBoundingClientRect();
    const toRect = toTooltip.value.getBoundingClientRect();
    return fromRect.right > toRect.left;
  }
</script>

<style scoped lang="scss">
  .time-range {
    position: relative;
    display: grid;
    background-color: var(--primaryColor);
    align-items: center;
    border-radius: 0.5rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;

    &.readonly {
      pointer-events: all;
    }

    &.preview {
      background-color: rgb(230, 230, 230);
      border-radius: 0;

      &.selecting {
        background-color: var(--actionsColorLighter);
      }
    }

    &.resizing,
    &.can-show-tooltip-on-hover:hover {
      background-color: var(--primaryColorDarkish);

      .right-handle,
      .left-handle {
        display: block;
      }
    }

    .right-handle,
    .left-handle {
      display: none;
      position: absolute;
      cursor: col-resize;
      border-radius: 0.5rem;
      width: 0.75rem;
      height: 100%;
      background-color: var(--primaryColorDark);

      &.disabled {
        pointer-events: none;
        background-color: transparent;
      }

      .handle-content {
        position: relative;
        height: 100%;
        width: 100%;
        display: inline-block;
        text-align: center;
      }

      .timestamp-tooltip {
        pointer-events: none;
        position: absolute;
        width: 6rem;
        height: 3rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        color: var(--primaryColorReverse);
        background-color: var(--primaryColorDark);
        border-radius: 10px;
        box-shadow: 0 0 1px 1px rgba(128, 128, 128, 0.5);
        top: -3.5rem;
        left: -3rem;
        z-index: 1000;

        &.flipped {
          top: 3rem;

          &::before {
            top: -0.5rem;
          }
        }
      }

      .timestamp-tooltip::before {
        content: '';
        position: absolute;
        transform: rotate(45deg);
        background-color: var(--primaryColorDark);
        padding: 0.5rem;
        top: calc(100% - 0.5rem);
        left: calc(50% - 0.25rem);
      }
    }

    .left-handle {
      left: 0;
    }

    .right-handle {
      right: 0;
    }
  }
</style>
